import { Component, forwardRef } from 'react';
import { DefaultTheme, withTheme } from 'styled-components';
import { getSpacingToken, handleRefSplit } from '@optimus-web/common';
import { Field } from '../Field';
import { ShadowElement, TextareaContainerElement, TextareaPlaceholder, ValueContainerElement } from './style';
import { TextareaElement, TextareaElementProps } from './TextareaElement';

export interface TextareaProps extends TextareaElementProps {
    forwardedRef?: React.Ref<HTMLTextAreaElement>;
    onHeightChange?: (height: string) => void;
    rows?: number;
    autoCollapse?: boolean;
    collapsedHeight?: number;
    maxHeight?: number;
    inline?: boolean;
    onBlur?: (e: React.FocusEvent) => void;
    onFocus?: (e: React.FocusEvent) => void;
    autosize?: boolean;
    theme?: DefaultTheme;
    getValue?: (value: string) => void;
}

class TextareaComponent extends Component<TextareaProps> {
    static defaultProps = {
        rows: 3,
        maxHeight: Infinity,
        autosize: false,
    };

    readonly state = {
        collapsed: false,
    };

    private autosizeTextarea?: HTMLTextAreaElement;

    componentDidMount() {
        if (this.props.autoCollapse) {
            this.collapse();
        } else {
            this.handleResize();
        }
        if (this.props.getValue && this.props.value !== undefined) {
            this.props.getValue(this.props.value);
        }
    }

    componentDidUpdate(prevProps: TextareaElementProps) {
        if (this.props.value !== prevProps.value) {
            this.handleResize();
        }

        if (this.props.getValue && this.props.value !== undefined) {
            this.props.getValue(this.props.value);
        }
    }

    handleResize = (animate = false) => {
        if (!this.autosizeTextarea || !this.props.autosize) {
            return;
        }

        // compute the height difference between inner height and outer height
        const style = window.getComputedStyle(this.autosizeTextarea);
        const heightOffset =
            style.boxSizing === 'content-box'
                ? -(parseFloat(style.paddingTop || '0') + parseFloat(style.paddingBottom || '0'))
                : parseFloat(style.borderTopWidth || '0') + parseFloat(style.borderBottomWidth || '0');

        // resize the input to its content size
        this.autosizeTextarea.style.height = 'auto';
        const height =
            this.props.maxHeight && this.props.autosize
                ? `${Math.min(this.autosizeTextarea.scrollHeight + heightOffset, this.props.maxHeight)}px`
                : `auto`;

        if (animate) {
            this.autosizeTextarea.style.height = '0';
            window.requestAnimationFrame(() => this.setHeight(height));
        } else {
            this.autosizeTextarea.style.height = height;
        }

        if (this.props.onHeightChange) {
            this.props.onHeightChange(height);
        }
    };

    setHeight = (height: string) => {
        if (this.autosizeTextarea) {
            this.autosizeTextarea.style.height = height;
        }
    };

    moveCursorToEnd = () => {
        window.setTimeout(() => {
            if (this.autosizeTextarea) {
                if (this.autosizeTextarea.setSelectionRange) {
                    const lastIdx = this.autosizeTextarea.value.length;
                    this.autosizeTextarea.setSelectionRange(lastIdx, lastIdx);
                } else {
                    const val = this.autosizeTextarea.value;
                    this.autosizeTextarea.value = '';
                    this.autosizeTextarea.value = val;
                }
            }
        }, 30);
    };

    expand = () => {
        this.setState({ collapsed: false }, () => {
            if (this.autosizeTextarea) {
                this.moveCursorToEnd();
                this.autosizeTextarea.scrollTop = this.autosizeTextarea.scrollHeight;
            }
        });
        this.handleResize(true);
    };

    collapse = () => {
        this.setState({ collapsed: true });
        if (this.autosizeTextarea && this.props.collapsedHeight) {
            const height = `${Math.min(this.props.collapsedHeight, this.autosizeTextarea.scrollHeight)}px`;
            window.requestAnimationFrame(() => this.setHeight(height));
            this.autosizeTextarea.scrollTop = 0;
        }
    };

    handleFocus = (e: React.FocusEvent) => {
        if (this.state.collapsed) {
            this.expand();
        }

        if (this.props.onFocus) {
            this.props.onFocus(e);
        }
    };

    handleBlur = (e: React.FocusEvent) => {
        if (this.props.autoCollapse) {
            this.collapse();
        }

        if (this.props.onBlur) {
            this.props.onBlur(e);
        }
    };

    handleTextareaRef = (c: HTMLTextAreaElement) => (this.autosizeTextarea = c);

    render() {
        const {
            forwardedRef,
            value,
            placeholder,
            children,
            collapsedHeight,
            autoCollapse,
            onHeightChange,
            size,
            inline,
            autosize,
            disabled,
            theme,
            ...other
        } = this.props;

        const textareaProps = {
            ...other,
            disabled,
            value,
            showScrollbar: autoCollapse,
            ref: handleRefSplit({ ownRef: this.handleTextareaRef, forwardedRef }),
            onBlur: this.handleBlur,
            onFocus: this.handleFocus,
            inline,
            size,
            autosize,
        };

        const showShadow =
            this.state.collapsed &&
            collapsedHeight &&
            this.autosizeTextarea &&
            this.autosizeTextarea.scrollHeight > collapsedHeight + (theme ? getSpacingToken('100')({ theme }) : 8) * 2;

        return (
            <TextareaContainerElement>
                <ValueContainerElement>
                    {!value && (
                        <TextareaPlaceholder inputSize={size} disabled={disabled} aria-hidden>
                            {placeholder}
                        </TextareaPlaceholder>
                    )}
                    <TextareaElement {...textareaProps} data-test-autosize-text-area />
                    {children}
                </ValueContainerElement>
                {showShadow && <ShadowElement />}
            </TextareaContainerElement>
        );
    }
}

export const TextareaNotWrapped = forwardRef<HTMLTextAreaElement, TextareaProps>((props, ref) => (
    <TextareaComponent {...props} forwardedRef={ref} />
));

const TextareaWithField = Field(TextareaNotWrapped);
export const Textarea = withTheme(TextareaWithField);
